/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import RefreshIcon from '@mui/icons-material/Cached';
import LogoutIcon from '@mui/icons-material/Logout';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { JWT_ACCESS_TOKEN_KEY, JWT_REFRESH_TOKEN_KEY } from 'api';
import AccessHubspotButton from 'components/AccessHubspotButton';
import VerifyDomainDialog from 'routes/onboard/member_email/components/VerifyDomainDialog';
import { useGetUserQuery, useValidateDomainQuery } from 'store/api/version-api.slice';
import { getParameterByName } from 'utils';
import logo from 'assets/logo.png';

import {
	DirectorySettingsView,
	DiscountCodesView,
	GatedContentView,
	LinkedAccounts,
	MemberNotifications,
	MemberSignUpView,
	MemberTypesView,
	Profile
} from './components';
import { SubscriptionInformation, UpdatePaymentInformation } from './components/PaymentInformation';
import { styles } from './styles';

const DashboardRoute = () => {
	const { data: user, isLoading: isUserLoading } = useGetUserQuery();
	const { data: validationResponse, isFetching: isValidateLoading, refetch } = useValidateDomainQuery();

	const isDKIMSetupFailed = !isValidateLoading && !validationResponse?.result.valid;

	const [verifyDomainOpen, setVerifyDomainOpen] = useState(false);

	const closeVerifyDomainDialog = () => {
		setVerifyDomainOpen(false);
	};

	const onboardingStep = user?.next_url;

	const navigate = useNavigate();

	useEffect(() => {
		document.title = 'Member Center - Dashboard';

		window._hsq = window._hsq || [];

		const { _hsq } = window;
		_hsq.push(['setPath', '/dashboard']);
		_hsq.push(['trackPageView']);
		_hsq.push([
			'identify',
			{
				email: getParameterByName('email'),
				onboarding_stage: 'completed'
			}
		]);
	}, []);

	const handleLogout = () => {
		localStorage.removeItem(JWT_ACCESS_TOKEN_KEY);
		localStorage.removeItem(JWT_REFRESH_TOKEN_KEY);
		navigate('/login');
	};

	const shouldNavigateToOnboarding = !isUserLoading && user && onboardingStep !== 0;

	useEffect(() => {
		if (shouldNavigateToOnboarding) {
			navigate('/onboarding');
		}
	}, [navigate, shouldNavigateToOnboarding]);

	const handleDKIMSetup = () => {
		setVerifyDomainOpen(true);
	};

	const handleRefresh = e => {
		e.stopPropagation();
		refetch();
	};

	return (
		<Box sx={styles.root}>
			<CssBaseline />
			<Container maxWidth="lg">
				<Grid container spacing={2}>
					<Grid item xs={12} md={8}>
						<Box sx={styles.header}>
							<img src={logo} alt="Logo" />
						</Box>
						{isDKIMSetupFailed && (
							<Alert
								severity="warning"
								onClick={handleDKIMSetup}
								sx={{
									alignItems: 'center',
									cursor: 'pointer',
									'&:hover': {
										backgroundColor: '#f7dfbe'
									},
									'& .MuiAlert-message': {
										width: '100%'
									}
								}}
							>
								<Box display="flex" justifyContent="space-between" alignItems="center">
									Email Deliverability Issue: DKIM (DomainKeys Identification Mail) is not setup!
									<IconButton onClick={handleRefresh}>
										<RefreshIcon color="warning" />
									</IconButton>
								</Box>
							</Alert>
						)}
						<Box style={{ display: 'flex', flexDirection: 'column', marginTop: '32px', marginBottom: 0 }}>
							<AccessHubspotButton />
						</Box>
						<LinkedAccounts />
						<MemberTypesView />
						<DiscountCodesView />
						<MemberNotifications />
						<MemberSignUpView />
						<Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '32px' }}>
							<Typography variant="h4" color="primary" component={'span'} sx={styles.title}>
								BETA FEATURES:
							</Typography>
						</Box>
						<GatedContentView />
						<DirectorySettingsView />
					</Grid>
					<Grid item xs={12} md={4} sx={{ display: 'flex' }}>
						<Paper elevation={2} sx={styles.paper}>
							<Box sx={styles.block}>
								<Profile />
							</Box>
							<Divider variant={'inset'} />
							<UpdatePaymentInformation className={styles.offsetWrapper} />
							<SubscriptionInformation />
							<Button fullWidth variant={'outlined'} sx={styles.logout} onClick={handleLogout}>
								<LogoutIcon sx={{ marginRight: '5px' }} />
								Logout
							</Button>
						</Paper>
					</Grid>
				</Grid>
				<VerifyDomainDialog isOpen={verifyDomainOpen} onClose={closeVerifyDomainDialog} />
			</Container>
		</Box>
	);
};

export default DashboardRoute;
